import PropTypes from 'prop-types'
import { useRef } from 'react'
import Popover from 'components/Popover'
import Type from 'components/Type'
import { background as backgroundColor, text as textColor } from 'theme/palette'
import { borderWidth } from 'theme/globals'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { IconCloseSmall } from 'icons'
import { useAction } from 'utils/actions'

const SIZES = {
  shorter: { padding: [0, .5], minHeight: '15px' },
  short: { padding: [0.2, .7, 0.3], minHeight: '18px' },
  default: { padding: [0.5, 1.5], minHeight: '35px' },
  tall: { padding: [0.5, 1], minHeight: '45px' },
}

const StyledChip = styled.div.withConfig({
  shouldForwardProp: prop => ![
    'font',
    'fluid',
    'isDismissable',
    'minWidth',
    'selectable',
    'color',
    'borderWidth',
    'variant',
    'variantHover',
    'colorHover',
    'duoTone',
    'disabled',
    'borderColor',
    'borderAlpha',
    'gap',
    'wrap',
  ].includes(prop),
})`
  min-height: inherit;
  ${props => props.theme.typography[props.font]}
  line-height: 1;
  // line-height: 8px;
  ${props => props.theme.internalMixins.p(SIZES[props.size].padding)}
  ${props => (props.fluid ? `width: 100%;` : '')}
  display: inline-grid;
  ${props =>
    props.isDismissable
      ? `grid-template-columns: 13px 1fr${props.minWidth ? ' 13px' : ''};`
      : 'grid-auto-flow: column;'}
  grid-auto-flow: column;
  grid-gap: ${props => props.gap};
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  border-radius: 45px;
  -webkit-touch-callout: none;
  user-select: none;
  // outline: 0;
  white-space: ${props => props.wrap ? 'initial' : 'nowrap'};
  ${props => (props.minWidth ? `min-width: ${props.minWidth};` : '')}
  border-style: solid;
  border-color: ${props => props.theme.internalMixins.hexToRgba(
      props.theme.palette.background[props.color]?.main,
      props.borderAlpha
    )};
  border-width: ${props => props.theme.globals.borderWidth[props.borderWidth]};
  cursor: ${props => (props.isDismissable || props.selectable ? 'pointer' : 'default')};
  background: ${props =>
    props.theme.palette.background[props.variant].main};
  color: ${props => props.theme.palette.background[props.variant].text};
  ${props =>
    props.color ? `color: ${props.theme.palette.text[props.color]};` : ''};
  transition: ${props =>
    props.theme.internalMixins.setTransition(['background', 'color'])};

  &:hover,
  &:active {
    ${props =>
      props.variantHover &&
      `
      background: ${
        props.theme.palette.background[props.variantHover].main
      };
      color: ${props.theme.palette.background[props.variantHover].text}; 
      ${
        props.colorHover
          ? `color: ${props.theme.palette.text[props.colorHover]};`
          : ''
      };
    `}
  }

  ${props =>
    props.duoTone
      ? `
    background: ${props.theme.internalMixins.hexToRgba(
      props.theme.palette.background[props.variant]?.main,
      0.2
    )};
    border-color: ${props.theme.palette.background[props.variant]?.main};
    color: ${props.theme.palette.background[props.variant]?.main};
  `
      : ''}

  ${props =>
    props.disabled
      ? `
    cursor: default;
    background: ${props.theme.palette.background.disabled.main};
    color: ${props.theme.palette.background.disabled.text};
    opacity: .6;
  `
      : ''}
`

const Chip = ({
  disabledText,
  disabled,
  justifyContent,
  size,
  onClose,
  onClick,
  onMouseUp,
  onMouseDown,
  children,
  ...props
}) => {
  const { openPopover, closePopover } = useAction()
  const buttonRef = useRef(null)

  const renderPopover = target => (
    <Popover target={target} position="bottom" variant="inverse" sizeX="auto">
      <Box p={1}>
        <Type variant="dataMicro">{disabledText}</Type>
      </Box>
    </Popover>
  )

  const handleMouseOver = () => {
    if (disabledText && disabled) openPopover(renderPopover, buttonRef?.current)
  }

  const handleMouseLeave = () => {
    if (disabledText) closePopover()
  }

  const handleKeyUp = e => {
    if (!disabled && e.which === 13) {
      onClick?.(e)
      onMouseUp?.(e)
      onMouseDown?.(e)
    }
  }

  return (
    <Stack
      ref={buttonRef}
      display="flex"
      justifyContent={justifyContent}
      minHeight={SIZES[size].minHeight}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <StyledChip
        tabIndex="0"
        onKeyUp={handleKeyUp}
        size={size}
        isDismissable={!!onClose}
        onClick={e => onClick?.(e)}
        onMouseDown={e => onMouseDown?.(e)}
        onMouseUp={e => onMouseUp?.(e)}
        {...props}
      >
        {onClose && (
          <Stack
            display="grid"
            justifyContent="center"
            alignItems="center"
            alignContent="center"
            sx={{
              cursor: 'pointer',
            }}
            onMouseUp={onClose}
          >
            <IconCloseSmall />
          </Stack>
        )}
        {children}
      </StyledChip>
    </Stack>
  )
}

Chip.propTypes = {
  font: PropTypes.string,
  color: PropTypes.oneOf(Object.keys(textColor)),
  colorHover: PropTypes.oneOf(Object.keys(textColor)),
  href: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(backgroundColor)),
  variantHover: PropTypes.oneOf(Object.keys(backgroundColor)),
  fluid: PropTypes.bool,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  onClick: PropTypes.func,
  selectable: PropTypes.bool,
  disabledText: PropTypes.string,
  disabled: PropTypes.bool,
  onDisabledClick: PropTypes.func,
  onMouseUp: PropTypes.func,
  onMouseDown: PropTypes.func,
  onClose: PropTypes.func,
  justifyContent: PropTypes.string,
  borderAlpha: PropTypes.number,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  duoTone: PropTypes.bool,
  minWidth: PropTypes.string,
  gap: PropTypes.string,
  wrap: PropTypes.bool,
}

Chip.defaultProps = {
  font: 'chipLabel',
  variant: 'primary',
  size: 'default',
  justifyContent: 'center',
  default: 1,
  borderColor: 'primary',
  borderWidth: 'none',
  gap: '10px',
}

export default Chip
